import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HttpError } from '~/components/Error/HttpError';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';

export type TosTermType = 'CITIZEN' | 'MARKET' | 'MEMBER' | 'DELETE';
export type PpTermType = 'PP_SIGNUP' | 'PP' | 'PI_3RD';
export type MpTermType = 'MP';
export type CollectionTermType = TosTermType | PpTermType | MpTermType;

export type Collection = 'TOS' | 'PP' | 'MP' | 'TOS_OTHERS';

export type Term = {
  collection: Collection;
  type: CollectionTermType;
  version?: string;
  agree: boolean;
  agreedAt: Date;
};

/**
 * @TermType: "collection:type"
 */
export type TermType = string;

export type TermsParams = {
  termsType: TermType[];
};

export const termsUrl = '/api/citizen/v1/terms/agree';

export const transformTerm = (term: Term) => ({
  ...term,
  agreedAt: new Date(term.agreedAt),
});

export const getTerms = async (params?: TermsParams) => {
  const {
    data: { terms },
  } = await axios.get<{ terms: Term[] }>(termsUrl, { params });
  return terms.map((term) => transformTerm(term));
};

export const useTerms = <TData = Term[]>(
  params?: TermsParams,
  options?: UseDosiminQueryOptions<Term[], HttpError, TData>
) =>
  useQuery<Term[], HttpError, TData>({
    queryKey: ['terms', params],
    queryFn: () => getTerms(params),
    initialData: [],
    ...options,
  });

export const useTerm = (
  termType: TermType,
  options?: UseDosiminQueryOptions<Term[], HttpError, Term | undefined>
) =>
  useTerms<Term | undefined>(
    { termsType: [termType] },
    {
      ...options,
      select: (terms) => terms[0],
    }
  );

export const agreeTerm = async (termType?: TermType) => {
  const {
    data: { term },
  } = await axios.put<{ term?: Term }>(termsUrl, { termType });
  return term ? transformTerm(term) : null;
};
export const useAgreeTerm = (
  options?: UseDosiminMutationOptions<Term | null, HttpError, TermType>
) =>
  useMutation<Term | null, HttpError, TermType>({
    mutationFn: agreeTerm,
    ...options,
  });
